/**
 * Global browser error capture for user logs.
 *
 * Captures two categories of uncaught failures that never reach component
 * error boundaries:
 *   - window.onerror                → synchronous runtime exceptions
 *   - window.onunhandledrejection   → unhandled promise rejections
 *
 * This is intentionally cheap and passive: it does NOT swallow or suppress
 * errors — it just observes and emits a log record, then lets the browser
 * continue its normal handling (console printing, React error overlay, etc.).
 */

import { userLogClient } from "./userLogClient";
import { LogAction } from "./types";

let installed = false;

const describeValue = (v: unknown): string => {
	if (v == null) return String(v);
	if (typeof v === "string") return v;
	if (v instanceof Error) return v.message || v.name;
	try {
		return JSON.stringify(v);
	} catch {
		return Object.prototype.toString.call(v);
	}
};

export const installGlobalErrorListeners = () => {
	if (installed) return;
	installed = true;

	window.addEventListener("error", (event: ErrorEvent) => {
		userLogClient.logEvent({
			action: LogAction.APP_ERROR,
			error_code: "window.error",
			properties: {
				kind: "uncaught_exception",
				message: describeValue(event.error ?? event.message),
				filename: event.filename || null,
				lineno: event.lineno ?? null,
				colno: event.colno ?? null,
				stack: event.error instanceof Error ? (event.error.stack ?? null) : null,
			},
		});
	});

	window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
		const reason = event.reason;
		userLogClient.logEvent({
			action: LogAction.APP_ERROR,
			error_code: "unhandledrejection",
			properties: {
				kind: "unhandled_promise_rejection",
				message: describeValue(reason),
				stack: reason instanceof Error ? (reason.stack ?? null) : null,
			},
		});
	});
};
